/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CSSProperties } from "react";

import { Animate, Wrap, hasContent } from "@components/elements";
import { ANIMATION } from "@components/constants";
import { CosmosStackProps } from "@components/types";
import { css, cx } from "@panda/css";
import { Stack } from "@panda/jsx";
import { token } from "@panda/tokens";

import { CosmosDesc } from "./";

import type { CosmosVariableRichText } from "@components/elements";
import { CosmosEnumEdsGradients } from "@cosmos/hygraph-schema";
import { TitleStack } from "./TitleStack.molecule";

export interface TitleDescStackProps {
	animate?: boolean;
	description?: CosmosVariableRichText | null;
	stack?: CosmosStackProps;
	title?: CosmosVariableRichText | null;
	gradient?: CosmosEnumEdsGradients | null;
	className?: string;
}

interface CssVariables extends CSSProperties {
	"--gap"?: string;
}

export const isFullyWrappedInAnchor = (title?: CosmosVariableRichText | null): boolean => {
	if (!title?.html) return false;

	const trimmedHtml = title.html
		.trim()
		.replace(/^<(h[1-6]|p|div)[^>]*>/i, "")
		.replace(/<\/(h[1-6]|p|div)>$/i, "");

	const hrefMatch = trimmedHtml.match(/^<a[^>]*href=["']([^"']*)["'][^>]*>/i);
	const hrefValue = hrefMatch?.[1]?.trim();
	const hasValidHref = hrefValue && hrefValue.length > 0 && hrefValue !== "#";

	if (!hasValidHref) return false;

	const startsWithAnchor = /^<a[^>]*>/i.test(trimmedHtml);
	const endsWithAnchor = /<\/a>$/i.test(trimmedHtml);

	const anchorCount = (trimmedHtml.match(/<a[^>]*>/gi) || []).length;
	const closingAnchorCount = (trimmedHtml.match(/<\/a>/gi) || []).length;

	return startsWithAnchor && endsWithAnchor && anchorCount === 1 && closingAnchorCount === 1;
};

export const TitleDescStack = ({
	animate = false,
	className,
	description,
	stack,
	title,
	gradient,
}: TitleDescStackProps): React.JSX.Element | null => {
	const spacingToken = stack?.gap ? token(`spacing.${stack?.gap}`) : token("spacing.eds.dimension.16");
	const style: CssVariables = { "--gap": spacingToken };

	// Check if title and description have actual content (not just empty HTML tags or whitespace)
	const hasTitleContent = hasContent(title);
	const hasDescContent = hasContent(description);

	if (!hasTitleContent && !hasDescContent) return null;

	return (
		<Stack
			{...stack}
			className={cx(
				`title-desc-stack`,
				css({
					gap: "var(--gap)",
				}),
				className,
			)}
			justify={stack?.justify || "start"}
			style={style}
		>
			{hasTitleContent ? (
				<Wrap
					condition={animate}
					wrap={(children) => (
						<Animate delay={ANIMATION.delay.title} type="slide">
							{children}
						</Animate>
					)}
				>
					<TitleStack gradient={gradient} title={title} />
				</Wrap>
			) : (
				""
			)}
			{hasDescContent ? (
				<Wrap
					condition={animate}
					wrap={(children) => (
						<Animate delay={ANIMATION.delay.description} type="slide">
							{children}
						</Animate>
					)}
				>
					<CosmosDesc description={description} />
				</Wrap>
			) : (
				""
			)}
		</Stack>
	);
};
